import { useEffect, useRef } from "react";
import useClickOutside from "@core/utils/clickOutside/useClickOutside";
import { createPortal } from "react-dom";
import { cn } from "@core/utils/cn";
import { cva } from "class-variance-authority";
import Button from "../Button";
import Icon from "../Icon";

const overlayStyles = cva(
  "fixed inset-0 z-[200] flex items-center justify-center",
  {
    variants: {},
  },
);

const modalStyles = cva(
  "relative bg-white rounded-xl shadow-lg overflow-hidden flex flex-col",
  {
    variants: {
      size: {
        sm: "w-full max-w-sm",
        md: "w-full max-w-md",
        lg: "w-full max-w-lg",
        xl: "w-full max-w-xl",
      },
    },
    defaultVariants: {
      size: "md",
    },
  },
);

interface ModalProps {
  onClose?: () => void;
  title?: string;
  size?: "sm" | "md" | "lg" | "xl";
  minWidth?: string;
  maxHeight?: string;
  className?: string;
  children: React.ReactNode;
  /** @deprecated No longer used — close button is now consumer responsibility */
  isLoading?: boolean;
}

const Modal = ({
  onClose,
  title,
  size = "md",
  minWidth,
  maxHeight,
  className,
  children,
}: ModalProps) => {
  const modalRef = useRef<HTMLDivElement>(null);
  const titleId = title ? "modal-title" : undefined;

  // Body scroll lock
  useEffect(() => {
    const original = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = original;
    };
  }, []);

  // Escape key handler
  useEffect(() => {
    if (!onClose) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [onClose]);

  // Focus trap
  useEffect(() => {
    const modal = modalRef.current;
    if (!modal) return;

    const focusableSelectors = [
      "a[href]",
      "button:not([disabled])",
      "input:not([disabled])",
      "textarea:not([disabled])",
      "select:not([disabled])",
      '[tabindex]:not([tabindex="-1"])',
    ].join(", ");

    const focusableElements =
      modal.querySelectorAll<HTMLElement>(focusableSelectors);
    const first = focusableElements[0];
    const last = focusableElements[focusableElements.length - 1];

    // Focus first element on mount
    first?.focus();

    const handleTab = (e: KeyboardEvent) => {
      if (e.key !== "Tab") return;
      if (focusableElements.length === 0) {
        e.preventDefault();
        return;
      }
      if (e.shiftKey) {
        if (document.activeElement === first) {
          e.preventDefault();
          last?.focus();
        }
      } else {
        if (document.activeElement === last) {
          e.preventDefault();
          first?.focus();
        }
      }
    };

    document.addEventListener("keydown", handleTab);
    return () => document.removeEventListener("keydown", handleTab);
  }, []);

  useClickOutside([modalRef], () => onClose?.());

  return createPortal(
    <div
      className={overlayStyles()}
      style={{ background: "var(--color-overlay)" }}

      aria-modal="true"
      role="dialog"
      aria-labelledby={titleId}
    >
      <div
        ref={modalRef}
        className={cn(modalStyles({ size }), className)}
        style={{
          minWidth: minWidth,
          maxHeight: maxHeight,
        }}
      >
        <div className="px-4 py-2 border-b-[1px] border-neutral-200 flex items-center justify-between">
          {title && (
            <div className="">
              <h4 id="modal-title" className="font-semibold text-neutral-900">
                {title}
              </h4>
            </div>
          )}
          <Button
            onClick={onClose}
            variant={"text"}
            size={"sm"}
            className="border-0 border-none text-neutral-400"
          >
            <Icon name={"circle-x"} className="size-5" strokeWidth={1.6} />
          </Button>
        </div>
        {children}
      </div>
    </div>,
    document.body,
  );
};

export default Modal;
